import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Sun, CloudSun, Moon, Camera, Quote, Flame, Clock, ChevronRight } from "lucide-react";

const TIME_ICONS = {
  morning: <Sun className="h-3.5 w-3.5 text-[#FFD66B]" />,
  afternoon: <CloudSun className="h-3.5 w-3.5 text-[#FF9F66]" />,
  evening: <Moon className="h-3.5 w-3.5 text-[#A78BFA]" />,
};

const TIME_LABEL = { morning: "Morning", afternoon: "Afternoon", evening: "Evening" };

export const DayByDay = ({ itinerary }) => {
  const { id } = useParams();
  const itineraryId = id || itinerary.id;
  const grouped = useMemo(() => groupSpots(itinerary), [itinerary]);

  if (!grouped.length) return null;

  return (
    <div className="space-y-10">
      {grouped.map((day, idx) => (
        <motion.div
          key={day.day}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.5, delay: idx * 0.05 }}
          className="relative"
        >
          <div className="flex items-baseline gap-3 mb-5">
            <div className="font-display font-black text-5xl sm:text-6xl text-gradient leading-none">D{day.day}</div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">Day {day.day}</div>
              <div className="font-display font-bold text-xl">{day.spots.length} {day.spots.length === 1 ? "spot" : "spots"}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {["morning", "afternoon", "evening"].map((slot) => {
              const slotSpots = day.spots.filter((s) => s.time_of_day === slot);
              if (slotSpots.length === 0) return (
                <SlotEmpty key={slot} slot={slot} />
              );
              return (
                <div key={slot} className="space-y-3">
                  <SlotHeader slot={slot} />
                  {slotSpots.map((s) => (
                    <SlotSpot key={s.id} spot={s} itineraryId={itineraryId} />
                  ))}
                </div>
              );
            })}
          </div>
        </motion.div>
      ))}
    </div>
  );
};

function groupSpots(itinerary) {
  const map = new Map();
  for (const s of itinerary.spots || []) {
    const d = s.day || 1;
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(s);
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([day, spots]) => ({ day, spots }));
}

const SlotHeader = ({ slot }) => (
  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-zinc-400">
    {TIME_ICONS[slot]} {TIME_LABEL[slot]}
  </div>
);

const SlotEmpty = ({ slot }) => (
  <div className="space-y-3 opacity-50">
    <SlotHeader slot={slot} />
    <div className="glass rounded-2xl p-4 text-xs text-zinc-500">Free time / explore</div>
  </div>
);

const SlotSpot = ({ spot, itineraryId }) => (
  <Link
    to={`/spot/${itineraryId}/${spot.id}`}
    data-testid={`day-spot-${spot.id}`}
    className="group block glass rounded-2xl overflow-hidden hover:border-white/25 transition"
  >
    <div className="relative aspect-[16/10] overflow-hidden">
      <img src={spot.image} alt={spot.name} className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
      <div className="absolute inset-0 scrim-bottom" />
      <div className="absolute top-2 right-2 glass rounded-full px-2 py-0.5 inline-flex items-center gap-1">
        <Flame className="h-3 w-3 text-[#FE0979]" />
        <span className="font-display font-bold text-[10px]">{spot.viral_score}</span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-3">
        <div className="font-display font-bold text-base leading-tight text-shadow-soft">{spot.name}</div>
      </div>
    </div>
    <div className="px-4 py-3 space-y-2">
      <Row icon={<Sparkles />} label="Why it's viral">
        {spot.content_hook}
      </Row>
      <Row icon={<Camera />} label="Best shot">
        {spot.photo_angle}
      </Row>
      <Row icon={<Quote />} label="Caption">
        <span className="italic">"{spot.reel_caption}"</span>
      </Row>
      <Row icon={<Clock />} label="Best time">
        {spot.best_time}
      </Row>
      <div className="flex items-center justify-end pt-1 text-[10px] uppercase tracking-[0.22em] text-zinc-300 group-hover:text-white">
        Open spot <ChevronRight className="h-3 w-3 ml-0.5" />
      </div>
    </div>
  </Link>
);

const Sparkles = (props) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3 text-[#FE0979]" {...props}>
    <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M5.6 18.4l2.1-2.1M16.3 7.7l2.1-2.1" />
  </svg>
);

const Row = ({ icon, label, children }) => (
  <div className="flex gap-2">
    <div className="shrink-0 mt-0.5">{icon}</div>
    <div className="text-xs leading-relaxed text-zinc-200 font-body">
      <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-0.5">{label}</span>
      {children}
    </div>
  </div>
);
